// Utilities Per Pound Settings Component
// Admin-only page: one ON/OFF switch per plant.
//
// When a plant is ON, its Water/lb, KWH/lb and Therms/lb rows appear on the Flash
// Report dashboard for everyone who can see that plant, AND in the Thursday GM
// Flash email. While it is OFF, only admins (dashboard) and the Early email
// list see its figures, in italics, as a preview. Every plant starts OFF.
// (Email added back by Jay, 2026-10-01 -- see exportRoutes.js Step 3a.)
// Backend: backend/routes/settings.js  (fail-closed -- see utils/plantSettings.js)

import React, { useState, useEffect } from 'react';
import { settingsAPI } from '../utils/api';
import '../styles/UtilitiesPerLbSettings.css';

// ---------------------------------------------------------------------------
// "Last changed" timestamp.
// updated_at is SYSDATETIME() on the SQL Server, i.e. PACIFIC wall-clock time,
// but the mssql driver hands it back labelled as UTC ("...Z"). Formatting it
// in the browser's own timezone would shift it by hours, so format it AS UTC,
// which reproduces the server's Pacific wall clock, and say "PT".
// ---------------------------------------------------------------------------
const formatChanged = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    if (isNaN(d)) return null;
    return d.toLocaleString('en-US', {
        timeZone: 'UTC', month: 'short', day: 'numeric', year: 'numeric',
        hour: 'numeric', minute: '2-digit'
    }) + ' PT';
};

function UtilitiesPerLbSettings() {
    const [plants, setPlants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [savingId, setSavingId] = useState(null);           // plant currently saving
    const [message, setMessage] = useState({ text: '', type: '' });

    // Load every plant's switch when the page opens
    useEffect(() => {
        loadSettings();
    }, []);

    const loadSettings = async () => {
        try {
            setLoading(true);
            const data = await settingsAPI.getUtilitiesPerLb();
            setPlants(data.plants || []);
        } catch (error) {
            setMessage({ text: 'Error loading Utilities Per Pound settings', type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    // -----------------------------------------------------------------------
    // Flip one plant. Turning ON asks first, because ON is the direction that
    // reveals figures to the GMs. The switch only moves after the server
    // confirms the save, so the page never shows a state that was not stored.
    // -----------------------------------------------------------------------
    const handleToggle = async (plant) => {
        const turningOn = !plant.enabled;
        if (turningOn && !window.confirm(
            `Turn ON Utilities Per Pound for ${plant.plant_name}?\n\n` +
            `Its Water/lb, KWH/lb and Therms/lb will show on the Flash Report ` +
            `dashboard for everyone who can see ${plant.plant_name}, not just admins, ` +
            `and in the Thursday GM Flash email.`
        )) {
            return;
        }

        try {
            setSavingId(plant.plant_id);
            setMessage({ text: '', type: '' });
            await settingsAPI.setUtilitiesPerLb(plant.plant_id, turningOn);
            await loadSettings();                                 // re-read: shows who/when from the server
            setMessage({
                text: `Utilities Per Pound for ${plant.plant_name} is now ${turningOn ? 'ON' : 'OFF'}.`,
                type: 'success'
            });
        } catch (error) {
            const reason = error.response?.data?.message || 'the server did not save it';
            setMessage({ text: `Could not change ${plant.plant_name}: ${reason}. Nothing was changed.`, type: 'error' });
        } finally {
            setSavingId(null);
        }
    };

    return (
        <div className="upl-container">
            {/* ---- Header + what the switch does ---- */}
            <div className="upl-header">
                <h2>Utilities Per Pound</h2>
                <p className="upl-intro">
                    Choose which plants show <strong>Water/lb</strong>, <strong>KWH/lb</strong> and{' '}
                    <strong>Therms/lb</strong> on the Flash Report dashboard and in the Thursday
                    GM Flash email. While a plant is OFF, only admins and the Early email see its
                    figures (in italics, as a preview). Plants start OFF.
                </p>
            </div>

            {message.text && (
                <div className={`upl-message upl-message-${message.type}`} role="status">
                    {message.text}
                </div>
            )}

            {/* ---- One row per plant ---- */}
            {loading && plants.length === 0 ? (
                <div className="upl-loading">Loading…</div>
            ) : (
                <div className="upl-table-container">
                    <table className="upl-table">
                        <thead>
                            <tr>
                                <th>Plant</th>
                                <th>Code</th>
                                <th>Utilities Per Pound</th>
                                <th>Last Changed</th>
                            </tr>
                        </thead>
                        <tbody>
                            {plants.map(plant => {
                                const changed = formatChanged(plant.updated_at);
                                const saving = savingId === plant.plant_id;
                                return (
                                    <tr key={plant.plant_id}>
                                        <td className="upl-plant">{plant.plant_name}</td>
                                        <td>{plant.plant_code}</td>
                                        <td>
                                            <label className="upl-switch">
                                                <input
                                                    type="checkbox"
                                                    checked={Boolean(plant.enabled)}
                                                    disabled={savingId !== null}
                                                    onChange={() => handleToggle(plant)}
                                                    aria-label={`Utilities Per Pound for ${plant.plant_name}`}
                                                />
                                                <span className="upl-slider" aria-hidden="true"></span>
                                                <span className={`upl-state ${plant.enabled ? 'on' : 'off'}`}>
                                                    {saving ? 'Saving…' : (plant.enabled ? 'ON' : 'OFF')}
                                                </span>
                                            </label>
                                        </td>
                                        <td className="upl-changed">
                                            {changed
                                                ? <>{changed}{plant.updated_by_name ? ` · ${plant.updated_by_name}` : ''}</>
                                                : <span className="upl-never">Never changed</span>}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

export default UtilitiesPerLbSettings;
